import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationInfo {
  currentPage: number
  totalItems: number
  totalPages: number
  itemsPerPage: number
}

interface PaginationProps {
  pagination: PaginationInfo
  onPageChange: (page: number) => void
  itemName?: string
  showPageNumbers?: boolean
}

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  itemName = 'items',
  showPageNumbers = false
}) => {
  if (pagination.totalPages <= 1) return null

  const hasNext = pagination.currentPage < pagination.totalPages
  const hasPrev = pagination.currentPage > 1

  // Calculate visible page numbers for advanced pagination
  const getVisiblePages = () => {
    const pages: number[] = []
    const maxVisible = 5
    let start = Math.max(1, pagination.currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(pagination.totalPages, start + maxVisible - 1)
    
    // Adjust start if we're near the end
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1)
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    
    return pages
  }

  return (
    <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-card">
      <div className="flex items-center text-sm text-muted-foreground">
        <span>
          Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total {itemName})
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(pagination.currentPage - 1)}
          disabled={!hasPrev}
          className="p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Page numbers (if enabled) */}
        {showPageNumbers && (
          <>
            {/* First page */}
            {pagination.currentPage > 3 && (
              <>
                <button
                  onClick={() => onPageChange(1)}
                  className="px-3 py-1 rounded text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  1
                </button>
                {pagination.currentPage > 4 && (
                  <span className="text-muted-foreground">...</span>
                )}
              </>
            )}

            {/* Visible page range */}
            {getVisiblePages().map((page) => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  page === pagination.currentPage
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {page}
              </button>
            ))}

            {/* Last page */}
            {pagination.currentPage < pagination.totalPages - 2 && (
              <>
                {pagination.currentPage < pagination.totalPages - 3 && (
                  <span className="text-muted-foreground">...</span>
                )}
                <button
                  onClick={() => onPageChange(pagination.totalPages)}
                  className="px-3 py-1 rounded text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  {pagination.totalPages}
                </button>
              </>
            )}
          </>
        )}

        {/* Next button */}
        <button
          onClick={() => onPageChange(pagination.currentPage + 1)}
          disabled={!hasNext}
          className="p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export default Pagination 